/**
 * Platform Users Page - Super Admin Global User Management
 * 
 * This page is ONLY accessible from the Super Admin console.
 * It allows managing platform-level users including:
 * - Viewing all users (with is_super_admin and is_owner flags)
 * - Creating new Super Admin users
 * - Activating/deactivating users
 * 
 * SECURITY: Protected by requireSuperAdmin middleware on backend
 * and requireSuperAdmin prop on frontend route.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Users, Shield, ShieldCheck, UserPlus, Search, RefreshCw,
  CheckCircle, XCircle, Eye, EyeOff, Loader2, Building2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const PlatformUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, super_admins: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSuperAdmin, setFilterSuperAdmin] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Create Super Admin Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: ''
  });

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterSuperAdmin !== 'all') params.append('is_super_admin', filterSuperAdmin);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.data.users || []);
      setStats(response.data.stats || { total: 0, super_admins: 0, active: 0, inactive: 0 });
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterSuperAdmin, filterStatus]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchUsers]);

  // Create Super Admin
  const handleCreateSuperAdmin = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setCreateLoading(true);
    try {
      const response = await api.post('/admin/users/super-admin', formData);
      toast.success(`Super Admin "${response.data.user.email}" créé avec succès`);
      setShowCreateDialog(false);
      setFormData({ first_name: '', last_name: '', email: '', password: '', phone: '' });
      fetchUsers();
    } catch (error) {
      console.error('Create super admin error:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setCreateLoading(false);
    }
  };

  // Toggle user active status
  const toggleUserStatus = async (user) => {
    try {
      await api.put(`/admin/users/${user.id}`, { active: !user.active });
      toast.success(`Utilisateur ${user.active ? 'désactivé' : 'activé'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Get owner status from user's account links
  const isUserOwner = (user) => {
    // Check if user has any owner access in riad_accesses
    return user.riad_accesses?.some(access => access.is_owner === true);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-violet-600" />
            Utilisateurs Plateforme
          </h1>
          <p className="text-gray-500 mt-1">
            Gestion globale des utilisateurs et des super administrateurs
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="create-super-admin-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Créer un Super Admin
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total utilisateurs</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-violet-200 bg-violet-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-violet-600">Super Admins</p>
                <p className="text-2xl font-bold text-violet-700">{stats.super_admins}</p>
              </div>
              <ShieldCheck className="w-8 h-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Actifs</p>
                <p className="text-2xl font-bold text-green-700">{stats.active}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Inactifs</p>
                <p className="text-2xl font-bold text-red-700">{stats.inactive}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-users-input"
                />
              </div>
            </div>
            <div className="w-[180px]">
              <Select value={filterSuperAdmin} onValueChange={setFilterSuperAdmin}>
                <SelectTrigger data-testid="filter-super-admin">
                  <SelectValue placeholder="Type d'utilisateur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="true">Super Admins</SelectItem>
                  <SelectItem value="false">Utilisateurs standards</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="inactive">Inactifs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchUsers} data-testid="refresh-users-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des utilisateurs</CardTitle>
          <CardDescription>
            Tous les utilisateurs de la plateforme avec leurs rôles et statuts
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucun utilisateur trouvé
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Super Admin</TableHead>
                  <TableHead>Riads</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </div>
                        <span>{user.first_name} {user.last_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.active ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-red-100 text-red-700">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {isUserOwner(user) ? (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                          <Building2 className="w-3 h-3 mr-1" />
                          Oui
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.is_super_admin ? (
                        <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Super Admin
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.riad_accesses?.length > 0 ? (
                        <span className="text-sm text-gray-600">
                          {user.riad_accesses.length} riad{user.riad_accesses.length > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {user.createdAt ? format(new Date(user.createdAt), 'dd MMM yyyy', { locale: fr }) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleUserStatus(user)}
                        className={user.active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                        data-testid={`toggle-user-${user.id}`}
                      >
                        {user.active ? 'Désactiver' : 'Activer'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Super Admin Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-600" />
              Créer un Super Administrateur
            </DialogTitle>
            <DialogDescription>
              Ce nouvel utilisateur aura un accès complet à la plateforme SaaS.
              Cette action sera enregistrée dans les logs d'audit.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateSuperAdmin} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Jean"
                  required
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Dupont"
                  required
                  data-testid="input-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@example.com"
                required
                data-testid="input-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe * (min. 8 caractères)</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone (optionnel)</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+212 6 00 00 00 00"
                data-testid="input-phone"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Important :</strong> Ce compte aura un accès total à la plateforme, 
              incluant la gestion de tous les clients, riads, abonnements et données financières.
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                disabled={createLoading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-violet-600 hover:bg-violet-700"
                disabled={createLoading}
                data-testid="submit-create-super-admin"
              >
                {createLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Créer le Super Admin
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformUsersPage;
